import View from '@ckeditor/ckeditor5-ui/src/view'
import ViewCollection from '@ckeditor/ckeditor5-ui/src/viewcollection'
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview'
import FocusTracker from '@ckeditor/ckeditor5-utils/src/focustracker'
import FocusCycler, { FocusableView } from '@ckeditor/ckeditor5-ui/src/focuscycler'
import KeystrokeHandler from '@ckeditor/ckeditor5-utils/src/keystrokehandler'
import Locale from '@ckeditor/ckeditor5-utils/src/locale'
import { t } from '@ulule/localize'

import unanchorIcon from './theme/icons/unanchor.svg'
import pencilIcon from '@ckeditor/ckeditor5-core/theme/icons/pencil.svg'

import './theme/anchorAction.css'
import '@ckeditor/ckeditor5-ui/theme/components/responsive-form/responsiveform.css'

/**
 * The anchor actions view class. This view displays the anchor preview, allows
 * unanchoring or editing the anchor.
 */
export class AnchorActionView extends View {
  focusTracker: FocusTracker
  keystrokes: KeystrokeHandler
  unanchorButtonView: ButtonView
  editButtonView: ButtonView
  private _focusables: ViewCollection<FocusableView>
  private _focusCycler: FocusCycler

  constructor(locale: Locale) {
    super(locale)

    this.focusTracker = new FocusTracker()

    this.keystrokes = new KeystrokeHandler()

    this.unanchorButtonView = this._createButton(t('Unanchor'), unanchorIcon, 'unanchor')

    this.editButtonView = this._createButton(t('Edit anchor'), pencilIcon, 'edit')

    this._focusables = new ViewCollection()

    this._focusCycler = new FocusCycler({
      focusables: this._focusables,
      focusTracker: this.focusTracker,
      keystrokeHandler: this.keystrokes,
      actions: {
        // Navigate fields backwards using the Shift + Tab keystroke.
        focusPrevious: 'shift + tab',

        // Navigate fields forwards using the Tab key.
        focusNext: 'tab',
      },
    })

    this.setTemplate({
      tag: 'div',

      attributes: {
        class: ['ck', 'ck-anchor-actions', 'ck-responsive-form'],

        // https://github.com/ckeditor/ckeditor5-anchor/issues/90
        tabindex: '-1',
      },

      children: [this.editButtonView, this.unanchorButtonView],
    })
  }

  render(): void {
    super.render()

    const childViews = [this.editButtonView, this.unanchorButtonView]

    childViews.forEach((view) => {
      // Register the view as focusable.
      this._focusables.add(view)

      // Register the view in the focus tracker.
      if (view.element) {
        this.focusTracker.add(view.element)
      }
    })

    // Start listening for the keystrokes coming from #element.
    this.keystrokes.listenTo(this.element as any)
  }

  /**
   * Focuses the fist {@link #_focusables} in the actions.
   */
  focus(): void {
    this._focusCycler.focusFirst()
  }

  /**
   * Creates a button view.
   */
  _createButton(label: string, icon: string, eventName: string): ButtonView {
    const button = new ButtonView(this.locale)

    button.set({
      label,
      icon,
      tooltip: true,
    })

    button.delegate('execute').to(this, eventName)

    return button
  }
}
